import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabase"; // Make sure supabase is initialized

// Function to generate a 6-character alphanumeric code
const generateCode = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let code = "";
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

const ExamAdmin = () => {
  const [studentName, setStudentName] = useState("");
  const [status, setStatus] = useState("");
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch all existing codes from the DB
  const fetchCodes = async () => {
    try {
      const { data, error } = await supabase
        .from("student_codes")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCodes(data);
    } catch (err) {
      console.error("Fetch codes error:", err);
      setStatus("❌ Failed to fetch codes");
    }
  };

  useEffect(() => {
    fetchCodes();
  }, []);

  // Handle code generation
  const handleGenerate = async () => {
    if (!studentName.trim()) {
      setStatus("❌ Enter student name");
      return;
    }

    const newCode = generateCode();
    setLoading(true);
    setStatus("Generating code...");

    try {
      const { data, error } = await supabase
        .from("student_codes")
        .insert([{ student_name: studentName.trim(), code: newCode, used: false }])
        .select(); // important to get inserted row back

      if (error) throw error;

      console.log("Inserted row:", data);

      setStatus(`✅ Code generated for ${studentName}: ${newCode}`);
      setStudentName("");
      fetchCodes();
    } catch (err) {
      console.error("Insert error:", err);
      setStatus("❌ Error saving code. Check RLS or table schema!");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    alert(`Copied: ${code}`);
  };

  return (
    <div className="max-w-3xl mx-auto p-6 mt-12 bg-white dark:bg-slate-800 rounded shadow">
      <h2 className="text-2xl font-bold mb-4 text-center text-slate-900 dark:text-white">
        Exam Code Generator
      </h2>

      {status && (
        <p className={`mb-4 text-center ${loading ? "text-blue-600" : ""}`}>
          {status}
        </p>
      )}

      <div className="flex gap-2 mb-6">
        <input
          type="text"
          placeholder="Enter student name"
          value={studentName}
          onChange={(e) => {
            setStudentName(e.target.value);
            setStatus("");
          }}
          className="flex-1 p-2 border rounded text-center"
        />
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Generate
        </button>
      </div>

      <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">
        Generated Codes
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full table-auto border-collapse border border-slate-300 dark:border-slate-700">
          <thead>
            <tr className="bg-slate-100 dark:bg-slate-700">
              <th className="border border-slate-300 dark:border-slate-600 px-4 py-2">
                Name
              </th>
              <th className="border border-slate-300 dark:border-slate-600 px-4 py-2">
                Code
              </th>
              <th className="border border-slate-300 dark:border-slate-600 px-4 py-2">
                Used
              </th>
              <th className="border border-slate-300 dark:border-slate-600 px-4 py-2">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {codes.length ? (
              codes.map((c) => (
                <tr key={c.id} className="text-center">
                  <td className="border px-4 py-2">{c.student_name}</td>
                  <td className="border px-4 py-2">{c.code}</td>
                  <td className="border px-4 py-2">{c.used ? "Yes" : "No"}</td>
                  <td className="border px-4 py-2">
                    <button
                      onClick={() => handleCopy(c.code)}
                      className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Copy
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-4">
                  No codes generated yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExamAdmin;
